import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChevronRight, Edit, FileDownload, KeyboardArrowDown, Search } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { exportTableToExcel, type Column } from "@/components/DataTable";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useAuth } from "@/context/AuthContext";
import { UserRoles } from "@/api/user.service";
import { useNotification } from "@/context/NotificationContext";
import {
  historicoAjuste,
  listCenarios,
  listRelatorioComprador,
  promoverCenario as promoverCenarioApi,
  salvarAjustes as salvarAjustesApi,
  statusCalculo as statusCalculoApi,
  type Ajuste,
  type Cenario,
  type HistoricoAjusteItem,
} from "@/api/metas.service";
import { corSituacao, situacaoDe, statusCalculoDe } from "./metasStatus";
import {
  fracaoContribuicao,
  fracaoParticipacao,
  moeda,
  mesesAnteriores,
  normalizar,
  participacao,
  percentualContribuicao,
  reaisInteiros,
  rotuloMes,
  variacaoPercentual,
  type ItemContribuicao,
} from "./metasFormato";
import {
  ARVORE_VAZIA,
  PROFUNDIDADE_PADRAO,
  avaliador,
  caminhoDe,
  distribuirProporcional,
  mesmosValores,
  ordenarPorNome,
  rotuloNivel,
  somarHistoricos,
  type NoMeta,
  type Valores,
} from "./metasArvore";
import { useArvoreMetas } from "./useArvoreMetas";
import { useFluxoCenario } from "./useFluxoCenario";
import { podeEditarValores } from "./metasFluxo";
import { useVisaoGrupo, type NoGrupo } from "./useVisaoGrupo";
import { linhasRelatorioComercial, type LinhaRelatorioComercial } from "./metasRelatorioComercial";
import { linhasRelatorioComprador, type LinhaRelatorioComprador } from "./metasRelatorioComprador";
import { linhasRelatorioN4, type LinhaRelatorioN4 } from "./metasRelatorioN4";
import { exportarRelatorioN4Estilizado } from "./metasRelatorioN4Excel";

/** Teto do ajuste manual: R$ 100 bilhões. */
const META_MAXIMA = 1e11;

/**
 * Altura de cada linha do cabeçalho, em px. `stickyHeader` gruda toda célula
 * em `top: 0`, o que só funciona para uma linha só — com duas, a segunda
 * sobrepõe a primeira ao rolar. Fixando a altura das linhas dá pra compensar
 * a segunda com `top: ALTURA_LINHA_CABECALHO` em vez de deixar as duas
 * disputando o mesmo topo.
 */
const ALTURA_LINHA_CABECALHO = 40;

/** Quantos meses ficam visíveis de cada vez na janela de scroll própria dos meses anteriores. */
const MESES_VISIVEIS = 3;

/** Largura de cada coluna de mês, em px — usada tanto no cabeçalho quanto nas linhas. */
const LARGURA_COLUNA_MES = 150;

/** Largura da janela de scroll dos meses anteriores: MESES_VISIVEIS colunas por vez. */
const LARGURA_JANELA_MESES = LARGURA_COLUNA_MES * MESES_VISIVEIS;

/**
 * Largura reservada para o ícone de editar, em px. Aplicada também como
 * espaço reservado (invisível) nas colunas sem ícone (Original) para que os
 * números fiquem alinhados com as colunas de valor informado/participação.
 */
const LARGURA_ICONE_EDICAO = 28;

/**
 * Altura fixa de cada linha da árvore, em px. A tabela é virtualizada (ver
 * `LinhasVirtualizadas`): só as linhas na janela de scroll são montadas, e a
 * posição de cada uma sai de `índice × ALTURA_LINHA` — por isso o nome não
 * quebra linha (corta com reticências) e nada na linha passa dessa altura.
 */
const ALTURA_LINHA = 44;

/** Altura máxima da área de scroll da tabela, em px. */
const ALTURA_TABELA = 600;

/** Linhas montadas além das visíveis, acima e abaixo, pra o scroll rápido não mostrar buraco. */
const FOLGA_LINHAS = 15;

/**
 * Largura de cada coluna, em px. A tabela usa `table-layout: fixed`: com a
 * virtualização, a largura automática dependeria só das linhas montadas e as
 * colunas pulariam ao rolar.
 */
const LARGURAS_COLUNAS = [380, LARGURA_JANELA_MESES, 180, 130, 190, 150, 200, 160];
/** Quantas lojas o relatório por comprador consulta ao mesmo tempo (uma chamada por loja). */
const LOJAS_POR_LOTE_COMPRADOR = 2;

type ModoEdicao = "valor" | "percentual";
type ModoEdicaoGrupo = "valor" | "participacao";

interface KpiProps {
  titulo: string;
  valor: string;
  nota: string;
  destaque?: boolean;
}

function Kpi({ titulo, valor, nota, destaque }: KpiProps) {
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: destaque ? "primary.main" : "divider",
        bgcolor: (theme) => (destaque ? alpha(theme.palette.primary.main, 0.08) : "transparent"),
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {titulo}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, my: 0.75, fontVariantNumeric: "tabular-nums" }}>
        {valor}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {nota}
      </Typography>
    </Box>
  );
}

interface LinhaMetaProps {
  id: string;
  nivel: number;
  rotulo: string;
  nome: string;
  historico: number[];
  calculada: number;
  ajustada: number;
  /** Contribuição do ajuste calculada pelo motor — vem pronta do backend, não é `ajustada - calculada`. */
  contribuicao: number;
  /** % Contribuição já calculada por quem monta a linha (visitar/visitarGrupo/TOTAL) — ver `percentualContribuicao` em metasFormato.ts. */
  percentualContribuicao: string;
  paiOriginal: number;
  paiAjustado: number;
  expansivel: boolean;
  aberto: boolean;
  carregando: boolean;
  onToggle: () => void;
  /** Na visão Grupo edita o grupo todo (ver `aplicarEdicaoGrupo`), mas não mostra histórico de ajuste. */
  editavel: boolean;
  /** Só a raiz GRUPO não tem participação a editar (é 100%); omitido, segue `editavel`. */
  editavelPercentual?: boolean;
  ajusteProprio: boolean;
  /** Só chamados quando `editavel`/`ajusteProprio` habilitam a UI que os aciona — a visão Grupo (sempre somente leitura) simplesmente não os passa. */
  onEditarValor?: () => void;
  onEditarPercentual?: () => void;
  onHistorico?: () => void;
}

/** Uma linha da árvore de metas — usada tanto na visão por loja quanto na visão Grupo (consolidada). */
function LinhaMeta({
  nivel,
  rotulo,
  nome,
  historico,
  calculada,
  ajustada,
  contribuicao,
  percentualContribuicao,
  paiOriginal,
  paiAjustado,
  expansivel,
  aberto,
  carregando,
  onToggle,
  editavel,
  editavelPercentual = editavel,
  ajusteProprio,
  onEditarValor,
  onEditarPercentual,
  onHistorico,
}: LinhaMetaProps) {
  return (
    <TableRow
      sx={{
        height: ALTURA_LINHA,
        bgcolor: nivel === 0 ? "background.subtle" : "transparent",
        "& td": { fontWeight: nivel === 0 ? 700 : 400, py: 0, whiteSpace: "nowrap" },
      }}
    >
      <TableCell
        sx={{
          position: "sticky",
          left: 0,
          zIndex: 1,
          bgcolor: nivel === 0 ? "background.subtle" : "background.paper",
        }}
      >
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ pl: `${nivel * 16}px` }}>
          {carregando ? (
            <CircularProgress size={16} sx={{ mx: 1 }} aria-label={`Carregando itens de ${nome}`} />
          ) : (
            <IconButton
              size="small"
              onClick={onToggle}
              aria-label={`Expandir ou recolher ${nome}`}
              aria-expanded={aberto}
              sx={{ visibility: expansivel ? "visible" : "hidden" }}
            >
              {aberto ? <KeyboardArrowDown fontSize="small" /> : <ChevronRight fontSize="small" />}
            </IconButton>
          )}
          <Chip size="small" variant="outlined" label={rotulo} sx={{ height: 20, fontSize: "0.65rem", flexShrink: 0 }} />
          <Typography variant="body2" noWrap title={nome} sx={{ minWidth: 0 }}>
            {nome}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ p: 0, width: LARGURA_JANELA_MESES }}>
        <Box sx={{ overflow: "hidden", width: LARGURA_JANELA_MESES }}>
          <Stack direction="row" sx={{ transform: "translateX(var(--meses-scroll, 0px))", willChange: "transform" }}>
            {historico.map((venda, i) => (
              <Box
                key={i}
                sx={{ width: LARGURA_COLUNA_MES, flexShrink: 0, textAlign: "right", px: 2, fontVariantNumeric: "tabular-nums" }}
              >
                {moeda(venda)}
              </Box>
            ))}
          </Stack>
        </Box>
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
          {moeda(calculada)}
          {editavel && <Box sx={{ width: LARGURA_ICONE_EDICAO, height: LARGURA_ICONE_EDICAO }} />}
        </Stack>
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
          {participacao(calculada, paiOriginal)}
          {editavel && <Box sx={{ width: LARGURA_ICONE_EDICAO, height: LARGURA_ICONE_EDICAO }} />}
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ color: ajustada !== calculada ? "primary.main" : "inherit" }}>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
          {ajusteProprio ? (
            <Tooltip title="Ver quem alterou, quando e por qual valor">
              <Box
                component="button"
                type="button"
                onClick={onHistorico}
                sx={{
                  font: "inherit",
                  fontWeight: 700,
                  color: "primary.main",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  border: "1px solid",
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
                  borderRadius: 1,
                  px: 0.75,
                  py: 0.25,
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.18) },
                }}
                aria-label={`Ver histórico de alterações de ${nome}`}
              >
                {moeda(ajustada)}
              </Box>
            </Tooltip>
          ) : (
            <strong>{moeda(ajustada)}</strong>
          )}
          {editavel && (
            <IconButton
              size="small"
              onClick={onEditarValor}
              aria-label={`Editar valor de ${nome}`}
              sx={{ width: LARGURA_ICONE_EDICAO, height: LARGURA_ICONE_EDICAO }}
            >
              <Edit fontSize="inherit" />
            </IconButton>
          )}
        </Stack>
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
          {participacao(ajustada, paiAjustado)}
          {editavelPercentual ? (
            <IconButton
              size="small"
              onClick={onEditarPercentual}
              aria-label={`Editar percentual de ${nome}`}
              sx={{ width: LARGURA_ICONE_EDICAO, height: LARGURA_ICONE_EDICAO }}
            >
              <Edit fontSize="inherit" />
            </IconButton>
          ) : (
            editavel && <Box sx={{ width: LARGURA_ICONE_EDICAO, height: LARGURA_ICONE_EDICAO }} />
          )}
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ color: contribuicao < 0 ? "error.main" : "inherit" }}>
        {moeda(contribuicao)}
      </TableCell>
      <TableCell align="right">{percentualContribuicao}</TableCell>
    </TableRow>
  );
}

/**
 * Monta só as linhas da árvore que estão na janela de scroll de
 * `containerRef` (mais `FOLGA_LINHAS` de cada lado); o resto vira duas linhas
 * vazias com a altura somada, pra barra de scroll continuar do tamanho da
 * árvore inteira. Com todas as lojas abertas até o último nível são dezenas
 * de milhares de linhas — montar todas travava a tela.
 *
 * O scroll é acompanhado aqui dentro, não em quem monta `linhas`: rolar
 * re-renderiza só este componente, e as linhas que continuam na janela são os
 * mesmos elementos de antes — o React nem as visita de novo.
 */
function LinhasVirtualizadas({
  linhas,
  containerRef,
  alturaCabecalho,
  colunas,
}: {
  linhas: React.ReactNode[];
  containerRef: React.RefObject<HTMLDivElement | null>;
  alturaCabecalho: number;
  colunas: number;
}) {
  const visiveis = Math.ceil(ALTURA_TABELA / ALTURA_LINHA);
  const [inicioJanela, setInicioJanela] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const aoRolar = () => {
      const topo = Math.max(0, container.scrollTop - alturaCabecalho);
      setInicioJanela(Math.max(0, Math.floor(topo / ALTURA_LINHA) - FOLGA_LINHAS));
    };
    aoRolar();
    container.addEventListener("scroll", aoRolar, { passive: true });
    return () => container.removeEventListener("scroll", aoRolar);
  }, [containerRef, alturaCabecalho]);

  const fim = Math.min(linhas.length, inicioJanela + visiveis + 2 * FOLGA_LINHAS);
  const inicio = Math.min(inicioJanela, fim);
  const espaco = (quantas: number) => (
    <TableRow aria-hidden style={{ height: quantas * ALTURA_LINHA }}>
      <TableCell colSpan={colunas} sx={{ p: 0, border: 0 }} />
    </TableRow>
  );
  return (
    <>
      {inicio > 0 && espaco(inicio)}
      {linhas.slice(inicio, fim)}
      {fim < linhas.length && espaco(linhas.length - fim)}
    </>
  );
}

/** Uma linha exportável do Excel, em valores brutos — formatação de moeda/percentual fica a cargo de quem grava a planilha. */
interface LinhaExportavel {
  rotulo: string;
  historico: number[];
  calculada: number;
  participacaoOriginal: number | null;
  ajustada: number;
  participacaoContribuicao: number | null;
  diferenca: number;
  variacao: number | null;
}

/** Monta uma linha exportável a partir dos mesmos valores usados pela linha em tela — usada por `visitar`, `visitarGrupo` e a linha TOTAL. */
function linhaExportavel(
  rotulo: string,
  historico: number[],
  calculada: number,
  paiOriginal: number,
  ajustada: number,
  paiAjustado: number,
  contribuicao: number,
  itensContribuicao: ItemContribuicao[],
): LinhaExportavel {
  return {
    rotulo,
    historico,
    calculada,
    participacaoOriginal: fracaoParticipacao(calculada, paiOriginal),
    ajustada,
    participacaoContribuicao: fracaoParticipacao(ajustada, paiAjustado),
    diferenca: contribuicao,
    variacao: fracaoContribuicao(itensContribuicao),
  };
}

export function MetasManutencaoTab({
  temPapelCriador = false,
  onAjustesPendentes,
}: {
  /** Se o usuário tem o papel "Criador de Meta" (ou é admin): é o que libera ajustar sem fluxo de aprovação. */
  temPapelCriador?: boolean;
  onAjustesPendentes?: (pendentes: boolean) => void;
} = {}) {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  // Os relatórios cobrem todas as lojas e categorias: o operador com código ERP
  // (o antigo comprador) só vê o que é dele na tela.
  const podeExportarRelatorios = !(user?.role === UserRoles.Operador && !!user.codigo_erp?.trim());
  const { notify } = useNotification();

  const [cenarios, setCenarios] = useState<Cenario[]>([]);
  const [loadingCenarios, setLoadingCenarios] = useState(true);
  const [cenarioId, setCenarioId] = useState("");

  // A janela de meses anteriores tem scroll próprio: só o cabeçalho é
  // arrastável, e o deslocamento é replicado nas linhas via variável CSS no
  // wrapper da tabela — evita re-renderizar a árvore inteira a cada pixel
  // rolado.
  const wrapperTabelaRef = useRef<HTMLDivElement>(null);
  const mesesCabecalhoRef = useRef<HTMLDivElement>(null);

  // Os ajustes vivem no cliente até "Salvar ajustes": `ajustes` é o que se vê
  // na tela e `salvos` é o último `salvarAjustes` bem-sucedido — a partir do
  // que veio de `obter_ajustes` ao carregar o cenário.
  const [ajustes, setAjustes] = useState<Valores>({});
  const [salvos, setSalvos] = useState<Valores>({});
  const [processando, setProcessando] = useState<"salvar" | "publicar" | null>(null);
  // Ação que descartaria ajustes ainda não salvos: espera a confirmação antes de seguir.
  const [confirmando, setConfirmando] = useState<{ acao: "publicar" } | { acao: "trocar"; cenarioId: string } | null>(null);

  const [lojaFiltro, setLojaFiltro] = useState("todas");
  const [menuExportar, setMenuExportar] = useState<HTMLElement | null>(null);
  const [exportandoComercial, setExportandoComercial] = useState(false);
  const [exportandoComprador, setExportandoComprador] = useState(false);
  // Progresso do carregamento até o N4 (pedaços concluídos / total); `null` fora do relatório N4.
  const [progressoN4, setProgressoN4] = useState<{ feitos: number; total: number } | null>(null);
  const [somenteAlteradas, setSomenteAlteradas] = useState(false);

  // Visão Grupo: soma todas as lojas numa raiz só e, descendo, junta
  // categorias de mesmo nome entre lojas diferentes — editar um grupo aplica
  // o ajuste a todas as lojas dele (ver `aplicarEdicaoGrupo`). É a visão
  // inicial; o efeito de fim de carga abaixo a expande ao abrir o cenário.
  const [verPor, setVerPor] = useState<"loja" | "grupo">("grupo");

  const [editando, setEditando] = useState<NoMeta | null>(null);
  const [modoEdicao, setModoEdicao] = useState<ModoEdicao>("valor");
  const [campoEdicao, setCampoEdicao] = useState("");
  const [erroEdicao, setErroEdicao] = useState("");

  // Edição na visão Grupo: um valor (ou uma participação) para o grupo todo,
  // aplicado a cada membro (um nó por loja).
  const [editandoGrupo, setEditandoGrupo] = useState<NoGrupo | null>(null);
  const [modoGrupo, setModoGrupo] = useState<ModoEdicaoGrupo>("valor");
  const [campoGrupo, setCampoGrupo] = useState("");
  const [erroGrupo, setErroGrupo] = useState("");

  // Histórico de edição de um nó com ajuste próprio; carregado sob demanda ao
  // clicar no valor.
  const [historicoNo, setHistoricoNo] = useState<NoMeta | null>(null);
  const [historicoItens, setHistoricoItens] = useState<HistoricoAjusteItem[] | null>(null);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);

  useEffect(() => {
    // Zerar a seleção junto da lista: mantido, o cenário do cliente anterior
    // faria a revisão ser pedida com o id de cálculo dele sob o cliente novo.
    setLoadingCenarios(true);
    setCenarios([]);
    setCenarioId("");
    listCenarios(clientId)
      .then((carregados) => {
        setCenarios(carregados);
        if (carregados[0]?.id) setCenarioId(carregados[0].id);
      })
      .catch(() => notify("Não foi possível carregar os cenários.", "error"))
      .finally(() => setLoadingCenarios(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const cenario = cenarios.find((c) => c.id === cenarioId);
  // Com o fluxo de aprovação ativo, só edita quem calcula, com o próprio escopo em cálculo,
  // e a publicação passa a ser feita na aba Aprovações.
  const fluxo = useFluxoCenario(clientId, cenarioId || undefined);
  const fluxoAtivo = fluxo.estado?.fluxoAtivo === true;
  const bloqueadoPeloFluxo = fluxoAtivo && !podeEditarValores(fluxo.estado, user!.id);
  // Sem fluxo, vale o papel "Criador de Meta". Enquanto o estado do fluxo não
  // chega (ou falha, como para quem não é responsável nem criador), não libera.
  const semPermissao = !fluxo.estado || (!fluxoAtivo && !temPapelCriador);
  // Para o aviso: o que já se sabe que bloqueia, não a espera pelo estado do fluxo.
  const falhaTransitoria = fluxo.erro === "transitorio" && !fluxo.estado;
  const negadoSemFluxo = fluxo.erro === "negado" || (!!fluxo.estado && !fluxoAtivo && !temPapelCriador);

  // A árvore (carga incremental por nível, busca no servidor, "Exibir até")
  // vive em useArvoreMetas — ver esse arquivo para o desenho da paginação.
  // `onCarregado` inicializa os ajustes com o que veio de `obter_ajustes`
  // assim que uma árvore nova termina de carregar. A tela abre em Grupo, que
  // precisa do nível 1 de todas as lojas: vem já na carga inicial, numa ida só.
  const arv = useArvoreMetas(
    clientId,
    cenario,
    (valores) => {
      setAjustes(valores);
      setSalvos(valores);
    },
    1,
  );

  // Nova árvore, nova janela: volta o scroll dos meses anteriores para o começo.
  useEffect(() => {
    if (mesesCabecalhoRef.current) mesesCabecalhoRef.current.scrollLeft = 0;
    wrapperTabelaRef.current?.style.setProperty("--meses-scroll", "0px");
  }, [arv.arvore === null]);

  const aoRolarMeses = (e: React.UIEvent<HTMLDivElement>) => {
    wrapperTabelaRef.current?.style.setProperty("--meses-scroll", `-${e.currentTarget.scrollLeft}px`);
  };

  const nos = arv.arvore?.nos ?? {};
  const lojas = arv.arvore?.lojas ?? [];

  const aval = useMemo(() => avaliador(arv.arvore ?? ARVORE_VAZIA, ajustes), [arv.arvore, ajustes]);
  const { ajustada } = aval;
  const calculadaDe = (id: string) => nos[id]?.calculado ?? 0;
  const contribuicaoDe = (id: string) => nos[id]?.contribuicaoCalculada ?? 0;

  // Como no módulo (PKG_FLASH_META_CALC): ajustes e publicação valem para CALCULADO ou PROMOVIDO — republicar reaplica os ajustes e atualiza as metas.
  const somenteLeitura =
    (cenario?.status !== "CALCULADO" && cenario?.status !== "PROMOVIDO") || bloqueadoPeloFluxo || semPermissao;
  const alterado = (id: string) => ajustada(id) !== calculadaDe(id);
  const sujo = !mesmosValores(ajustes, salvos);

  // Quem hospeda a tela precisa saber, para avisar ao trocar de aba; ao sair, nada mais está pendente.
  useEffect(() => {
    onAjustesPendentes?.(sujo);
  }, [sujo]);
  useEffect(() => () => onAjustesPendentes?.(false), []);

  const global = lojas.reduce((acc, id) => acc + ajustada(id), 0);
  const calculadoTotal = lojas.reduce((acc, id) => acc + calculadaDe(id), 0);
  const lojasSelecionadas = lojas.filter((id) => lojaFiltro === "todas" || id === lojaFiltro);
  const totalSelecionado = lojasSelecionadas.reduce((acc, id) => acc + ajustada(id), 0);
  const totalOriginalSelecionado = lojasSelecionadas.reduce((acc, id) => acc + calculadaDe(id), 0);
  // Histórico por mês da linha de total: soma do histórico de cada loja
  // selecionada, mês a mês (a mesma janela de todas as lojas).
  const historicoTotalSelecionado = somarHistoricos(lojasSelecionadas, nos);
  const contribuicaoTotalSelecionada = lojasSelecionadas.reduce((acc, id) => acc + contribuicaoDe(id), 0);
  // Um item por loja selecionada pra `percentualContribuicao` — mesma soma de
  // `contribuicaoTotalSelecionada`, sem perder o percentual de cada nó.
  const itensContribuicaoTotal: ItemContribuicao[] = lojasSelecionadas.map((id) => ({
    percEnviado: nos[id]?.percContribuicaoEnviado ?? null,
    contribuicao: contribuicaoDe(id),
    calculada: calculadaDe(id),
  }));

  // A visão Grupo (consolidada entre lojas) é montada no cliente por cima da
  // mesma árvore — ver useVisaoGrupo para o agrupamento por nome.
  const visaoGrupo = useVisaoGrupo({
    lojas,
    nos,
    filhosCarregados: arv.arvore?.filhos ?? {},
    ajustada,
    calculadaDe,
    contribuicaoDe,
    carregando: arv.carregando,
    carregarFilhos: arv.carregarFilhos,
  });

  // Cada visão tem o seu "Exibir até": "Por loja" usa `arv.profundidade`/
  // `arv.expandidos`; o Grupo, `profundidadeGrupo`/`expandidosGrupo`. Os dois
  // só compartilham a árvore carregada (`arv.carregarNiveis`), nunca o que
  // está aberto — mudar um não mexe no outro. O Grupo abre já no N1: só com a
  // linha raiz não mostraria nada de útil.
  const [profundidadeGrupo, setProfundidadeGrupo] = useState(1);
  const profundidadeAtual = verPor === "grupo" ? profundidadeGrupo : arv.profundidade;

  const recolherTudo = () => {
    setSomenteAlteradas(false);
    if (verPor === "grupo") {
      arv.setBusca("");
      setProfundidadeGrupo(0);
      visaoGrupo.recolher();
    } else {
      arv.recolher();
    }
  };

  /** Muda "Exibir até" só da visão ativa. */
  const mudarProfundidade = async (nivel: number) => {
    if (verPor === "loja") {
      // Loja filtrada: só a subárvore dela vem do ERP — as outras nem aparecem.
      await arv.trocarProfundidade(nivel, lojaFiltro === "todas" ? undefined : lojaFiltro);
      return;
    }
    setProfundidadeGrupo(nivel);
    const novaArvore = nivel > 0 ? await arv.carregarNiveis(nivel) : arv.arvore;
    visaoGrupo.expandirAteNivel(nivel, novaArvore);
  };

  /**
   * Troca a loja filtrada em "Por loja" e reaplica o "Exibir até" atual sobre
   * ela — a carga do nível pedido é por loja, então a loja nova (ou todas)
   * ainda pode não ter os níveis que a anterior tinha.
   */
  const trocarLojaFiltro = (loja: string) => {
    setLojaFiltro(loja);
    if (arv.profundidade > 0) void arv.trocarProfundidade(arv.profundidade, loja === "todas" ? undefined : loja);
  };

  /**
   * Garante o Grupo com os níveis do "Exibir até" dele carregados de todas as
   * lojas numa chamada só (em vez de uma chamada por loja) e expandido até
   * lá — chamada ao entrar na visão e de novo, pelo efeito abaixo, quando uma
   * árvore nova termina de carregar com a visão já ativa (troca de cenário).
   */
  const garantirMinimoGrupo = async (nivel = profundidadeGrupo) => {
    const nivelAlvo = Math.max(nivel, 1);
    const novaArvore = await arv.carregarNiveis(nivelAlvo);
    visaoGrupo.expandirAteNivel(nivelAlvo, novaArvore);
  };

  // Guarda a transição carregandoRevisao=true→false pra reaplicar o Grupo
  // assim que a carga inicial de um cenário novo termina — sem isso, trocar
  // de cenário estando em Grupo deixaria a tela mostrando só a linha raiz. O
  // "Exibir até" do Grupo também volta ao padrão, como o de "Por loja".
  const carregandoRevisaoAnterior = useRef(arv.carregandoRevisao);
  useEffect(() => {
    const terminouDeCarregar = carregandoRevisaoAnterior.current && !arv.carregandoRevisao;
    carregandoRevisaoAnterior.current = arv.carregandoRevisao;
    if (!terminouDeCarregar) return;
    setProfundidadeGrupo(1);
    if (verPor === "grupo") void garantirMinimoGrupo(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arv.carregandoRevisao]);

  /**
   * Troca entre "Por loja" e "Grupo" — a visão que entra volta com o "Exibir
   * até" padrão dela (Loja em "Por loja", N1 no Grupo), não com o que ficou
   * da última vez que esteve aberta. A árvore já carregada é reaproveitada.
   */
  const trocarVerPor = (novo: "loja" | "grupo") => {
    setVerPor(novo);
    if (novo === "grupo") {
      setLojaFiltro("todas");
      setSomenteAlteradas(false);
      setProfundidadeGrupo(1);
      void garantirMinimoGrupo(1);
    } else {
      void arv.trocarProfundidade(PROFUNDIDADE_PADRAO);
    }
  };

  const alternarExpandido = (id: string, travado: boolean) => {
    // Num ramo aberto pela busca ou por "somente alteradas", recolher
    // esconderia justamente o que o filtro trouxe.
    if (travado) {
      notify("Limpe a busca e o filtro de alteradas para recolher a árvore.", "info");
      return;
    }
    arv.toggleNo(id);
  };

  const limparFiltros = () => {
    setLojaFiltro("todas");
    setSomenteAlteradas(false);
    if (verPor === "loja") {
      // `recolher` já limpa a busca junto.
      arv.recolher();
    } else {
      // Em "Grupo" não há loja/alteradas pra desfiltrar, só a busca — e só
      // ela, sem tocar profundidade/expandidos: um recolher completo aqui
      // derrubaria a visão abaixo do mínimo que o Grupo precisa pra mostrar
      // algo, sem o usuário ter pedido pra sair dela.
      arv.setBusca("");
    }
  };

  const abrirEdicao = (no: NoMeta, modo: ModoEdicao) => {
    setEditando(no);
    setModoEdicao(modo);
    setCampoEdicao(modo === "percentual" ? "0" : String(ajustada(no.id)));
    setErroEdicao("");
  };

  const abrirHistorico = async (no: NoMeta) => {
    if (!cenario?.id) return;
    setHistoricoNo(no);
    setHistoricoItens(null);
    setCarregandoHistorico(true);
    try {
      setHistoricoItens(await historicoAjuste(cenario.id, no.codigo, clientId, no.codEmpresa, no.tipoNodo));
    } catch {
      notify("Não foi possível carregar o histórico deste valor.", "error");
      setHistoricoNo(null);
    } finally {
      setCarregandoHistorico(false);
    }
  };

  /** A meta que o formulário está propondo, em reais inteiros — sempre para cima. */
  const alvoEdicao = () => {
    if (!editando) return NaN;
    const numero = Number(campoEdicao.replace(",", "."));
    if (!Number.isFinite(numero)) return NaN;
    return reaisInteiros(modoEdicao === "valor" ? numero : ajustada(editando.id) * (1 + numero / 100));
  };

  /**
   * Os ajustes que resultam de a meta (visível) de `no` passar a valer
   * `alvo`, partindo de `base` — pura, pra que a visão Grupo encadeie uma
   * loja/nó atrás do outro sobre o resultado do anterior.
   */
  const ajustesAposEdicao = (no: NoMeta, alvo: number, base: Valores): Valores => {
    const { [no.id]: _proprio, ...semAjusteProprio } = base;
    const semAjuste = avaliador(arv.arvore ?? ARVORE_VAZIA, semAjusteProprio).ajustada(no.id);

    // Voltar exatamente ao que o nó valeria sem ajuste próprio é desfazer o
    // ajuste, não gravar um ajuste "igual" — assim ele some do Salvar.
    if (alvo === semAjuste) return semAjusteProprio;

    // Fixar o total de `no` reescreve a subárvore (ela escala sobre o alvo):
    // os ajustes dos descendentes ficam superados. Já editar um descendente
    // não passa por aqui — ele sobe pelos ancestrais em `avaliador`.
    const nos = arv.arvore?.nos ?? {};
    const dentroDe = (id: string) => {
      for (let atual = nos[id]?.paiId ?? null; atual; atual = nos[atual]?.paiId ?? null) {
        if (atual === no.id) return true;
      }
      return false;
    };
    const semDescendentes = Object.fromEntries(Object.entries(semAjusteProprio).filter(([id]) => !dentroDe(id)));
    return { ...semDescendentes, [no.id]: alvo };
  };

  const aplicarEdicao = () => {
    if (!editando || !cenario || !arv.arvore) return;
    const alvo = alvoEdicao();
    if (!Number.isSafeInteger(alvo) || alvo < 0 || alvo > META_MAXIMA) {
      setErroEdicao("Informe uma meta válida, maior ou igual a zero e dentro do limite da tela.");
      return;
    }
    if (somenteLeitura) {
      setErroEdicao("Cenário somente para consulta.");
      return;
    }

    setAjustes(ajustesAposEdicao(editando, alvo, ajustes));
    setEditando(null);
    notify("Meta ajustada. Níveis abaixo, loja e total global recalculados.", "success");
  };

  // --- Edição na visão Grupo ---------------------------------------------
  // Valor informado: o novo total do grupo é redistribuído entre os membros
  // (um por loja) na proporção do que cada um vale hoje. Participação: o
  // percentual digitado vale para cada membro, sobre o pai dele na própria
  // loja. Ambos viram um ajuste por membro, pelo mesmo `ajustesAposEdicao`
  // da edição por loja.

  /** O que o membro vale hoje para quem edita — numa loja, sem as categorias N1 fixas de fora (igual à linha da árvore). */
  const valorAtualMembro = (id: string) => {
    const no = nos[id];
    return no ? ajustada(no.id) : 0;
  };
  const valorAtualGrupo = (grupo: NoGrupo) => grupo.membros.reduce((acc, id) => acc + valorAtualMembro(id), 0);
  const paiAjustadoGrupo = (grupo: NoGrupo) =>
    grupo.membros.reduce((acc, id) => acc + (nos[id]?.paiId ? ajustada(nos[id]!.paiId!) : 0), 0);

  const abrirEdicaoGrupo = (grupo: NoGrupo, modo: ModoEdicaoGrupo) => {
    setEditandoGrupo(grupo);
    setModoGrupo(modo);
    const pai = paiAjustadoGrupo(grupo);
    setCampoGrupo(modo === "valor" ? String(valorAtualGrupo(grupo)) : pai ? ((valorAtualGrupo(grupo) / pai) * 100).toFixed(2).replace(".", ",") : "0");
    setErroGrupo("");
  };

  /** Meta de cada membro de `editandoGrupo` para o que foi digitado; `null` = valor digitado inválido. */
  const alvosGrupo = (): number[] | null => {
    if (!editandoGrupo) return null;
    const numero = Number(campoGrupo.replace(",", "."));
    if (campoGrupo === "" || !Number.isFinite(numero) || numero < 0) return null;
    if (modoGrupo === "valor") {
      const total = reaisInteiros(numero);
      return distribuirProporcional(total, editandoGrupo.membros.map(valorAtualMembro));
    }
    if (numero >= 100) return null;
    const fracao = numero / 100;
    return editandoGrupo.membros.map((id) => {
      const paiId = nos[id]?.paiId;
      if (!paiId) return valorAtualMembro(id);
      const pai = ajustada(paiId);
      // Pai com ajuste próprio fica fixo: a participação é sobre o valor dele.
      // Sem ajuste próprio, o pai acompanha o membro (os irmãos não mudam),
      // então a participação que a tela vai mostrar é fracao = alvo / (resto + alvo).
      const resto = pai - ajustada(id);
      return reaisInteiros(ajustes[paiId] !== undefined ? fracao * pai : (fracao * resto) / (1 - fracao));
    });
  };
  const alvosDoGrupo = editandoGrupo ? alvosGrupo() : null;
  const alvosGrupoValidos = !!alvosDoGrupo && alvosDoGrupo.every((v) => Number.isSafeInteger(v) && v >= 0 && v <= META_MAXIMA);

  const aplicarEdicaoGrupo = () => {
    if (!editandoGrupo || !arv.arvore) return;
    if (somenteLeitura) {
      setErroGrupo("Cenário somente para consulta.");
      return;
    }
    const alvos = alvosGrupo();
    if (!alvos || !alvosGrupoValidos) {
      setErroGrupo(
        modoGrupo === "valor"
          ? "Informe uma meta válida, maior ou igual a zero e dentro do limite da tela."
          : "Informe uma participação de 0% até menos de 100%.",
      );
      return;
    }
    let proximos = ajustes;
    editandoGrupo.membros.forEach((id, i) => {
      const no = nos[id];
      if (no) proximos = ajustesAposEdicao(no, alvos[i]!, proximos);
    });
    setAjustes(proximos);
    setEditandoGrupo(null);
    notify("Meta ajustada em todas as lojas do grupo. Níveis abaixo, lojas e total global recalculados.", "success");
  };

  /**
   * Grava um ajuste VENDA/MES por nó com ajuste próprio, no nível em que foi
   * feito — o motor propaga para baixo no recálculo. `salvar_ajustes`
   * substitui o conjunto inteiro do cenário (não faz merge), então isto
   * também é o que limpa um ajuste desfeito na tela.
   */
  const salvarAjustes = async () => {
    if (!cenario?.id) return;
    setProcessando("salvar");
    const lista: Ajuste[] = Object.entries(ajustes).flatMap(([id, valorInformado]) => {
      const no = nos[id];
      if (!no) return [];
      return [
        {
          // A mesma categoria/produto existe uma vez por loja: sem a loja,
          // ajustes de lojas diferentes colidem. A própria loja é única.
          ...(no.tipoNodo !== "EMPRESA" && no.codEmpresa !== null ? { codEmpresa: no.codEmpresa } : {}),
          codigoNodo: no.codigo,
          tipoNodo: no.tipoNodo,
          indicador: "VENDA" as const,
          alvo: "MES" as const,
          tipoValor: "VALOR" as const,
          valorInformado,
        },
      ];
    });
    try {
      await salvarAjustesApi(cenario.id, lista, clientId);
      setSalvos({ ...ajustes });
      notify("Ajustes salvos.", "success");
    } catch (err: any) {
      notify(err?.message || "Não foi possível salvar os ajustes.", "error");
    } finally {
      setProcessando(null);
    }
  };

  /** Publicar ignora o que ainda não foi salvo — avisa antes de seguir. */
  const pedirPublicar = () => {
    if (sujo) setConfirmando({ acao: "publicar" });
    else void publicar();
  };

  const trocarCenario = (novoId: string) => {
    setCenarioId(novoId);
    setEditando(null);
    setEditandoGrupo(null);
    setLojaFiltro("todas");
    arv.setBusca("");
    setSomenteAlteradas(false);
  };

  /** Trocar de cenário zera os ajustes da tela — avisa antes se houver algo não salvo. */
  const pedirTrocarCenario = (novoId: string) => {
    if (novoId === cenarioId) return;
    if (sujo) setConfirmando({ acao: "trocar", cenarioId: novoId });
    else trocarCenario(novoId);
  };

  const onConfirmarAcao = () => {
    if (!confirmando) return;
    const atual = confirmando;
    setConfirmando(null);
    if (atual.acao === "publicar") void publicar();
    else trocarCenario(atual.cenarioId);
  };

  /**
   * Dispara a publicação e responde na hora com o cenário em PROMOVENDO — o
   * merge roda no banco do cliente e passa dos 30s que a borda aceita, então
   * quem espera o desfecho é o acompanhamento logo abaixo, não esta chamada.
   */
  const publicar = async () => {
    if (!cenario?.id) return;
    setProcessando("publicar");
    try {
      const resultado = await promoverCenarioApi(cenario.id, clientId);
      setCenarios((prev) =>
        prev.map((c) => (c.id === cenario.id ? { ...c, status: resultado.status, erroCalculo: undefined } : c))
      );
      notify(`Publicação de "${cenario.descricao}" iniciada — pode levar alguns minutos.`, "info");
    } catch (err: any) {
      notify(err?.message || "Não foi possível publicar o cenário.", "error");
    } finally {
      setProcessando(null);
    }
  };

  // Todo cenário em PROMOVENDO é acompanhado até o back dar o desfecho: a
  // publicação roda fora do request que a disparou, então recarregar a tela no
  // meio não a interrompe — a listagem traz o PROMOVENDO de volta e o
  // acompanhamento recomeça daqui.
  const promovendoId = cenario?.status === "PROMOVENDO" ? cenario.id : null;
  const promovendoDescricao = cenario?.descricao;
  useEffect(() => {
    if (!promovendoId) return;

    let cancelado = false;
    let timer: ReturnType<typeof setTimeout>;
    const limite = Date.now() + 30 * 60 * 1_000;

    const ciclo = async () => {
      let situacao;
      try {
        situacao = await statusCalculoApi(promovendoId, clientId);
      } catch {
        // Resposta que não chegou não é desfecho: o ciclo seguinte pergunta de novo.
      }
      if (cancelado) return;
      if (situacao && situacao.status !== "PROMOVENDO") {
        setCenarios((prev) =>
          prev.map((c) =>
            c.id === promovendoId ? { ...c, status: situacao.status, erroCalculo: situacao.erroCalculo } : c
          )
        );
        if (situacao.erroCalculo) {
          notify(`Não foi possível publicar "${promovendoDescricao}": ${situacao.erroCalculo}`, "error");
        } else {
          notify(`Cenário "${promovendoDescricao}" publicado.`, "success");
        }
        return;
      }
      if (Date.now() >= limite) {
        notify("A publicação está demorando mais que o esperado. Recarregue a tela para ver como terminou.", "warning");
        return;
      }
      timer = setTimeout(ciclo, 5_000);
    };

    timer = setTimeout(ciclo, 5_000);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promovendoId, clientId]);

  const nomeCaminho = (no: NoMeta) =>
    caminhoDe(arv.arvore ?? ARVORE_VAZIA, no.id)
      .map((id) => nos[id]?.nome ?? "")
      .join(" / ");

  // Modo focado: busca e/ou "somente alteradas" definem os nós-alvo, e a
  // tabela mostra só o caminho até cada um (ancestrais sempre abertos, irmãos
  // fora do caminho escondidos). Do alvo para baixo a navegação é a normal.
  const foco = useMemo(() => {
    if (!arv.arvore) return null;
    let alvos: string[] | null = null;
    if (arv.resultadoBusca) alvos = [...arv.resultadoBusca.alvos];
    if (somenteAlteradas) {
      const proprios = Object.keys(ajustes);
      alvos = alvos ? alvos.filter((id) => alterado(id)) : proprios;
    }
    if (!alvos) return null;
    const noCaminho = new Set<string>();
    alvos.forEach((id) => caminhoDe(arv.arvore!, id).forEach((n) => noCaminho.add(n)));
    const filhosNoCaminho: Record<string, string[]> = {};
    noCaminho.forEach((id) => {
      const pai = arv.arvore!.nos[id]?.paiId;
      if (pai) (filhosNoCaminho[pai] ??= []).push(id);
    });
    Object.keys(filhosNoCaminho).forEach((pai) => {
      filhosNoCaminho[pai] = ordenarPorNome(filhosNoCaminho[pai]!, arv.arvore!.nos);
    });
    return { alvos: new Set(alvos), noCaminho, filhosNoCaminho };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arv.arvore, arv.resultadoBusca, somenteAlteradas, ajustes]);

  /**
   * Modo focado da visão Grupo: mesmo princípio de `foco` (busca só — sem
   * "somente alteradas", que não existe aqui), mas com o caminho traduzido
   * para ids sintéticos (raiz + `${pai}/${tipoNodo}:${nome}` por nível, o
   * mesmo esquema de `useVisaoGrupo`). O nó de um acerto entra na árvore sem
   * `filhos` carregado (`incorporar`, não `aplicarCarga`), então os grupos do
   * caminho não podem vir de `filhosDeGrupo` — os `membros` de cada nível são
   * juntados aqui, ids reais de lojas diferentes cujo caminho por nome bate
   * no mesmo grupo (a API já devolve um acerto por loja onde o termo bate,
   * então a soma sai correta sem precisar carregar as demais).
   */
  const focoGrupo = useMemo(() => {
    if (!arv.arvore || !arv.resultadoBusca) return null;
    const raizId = visaoGrupo.raizGrupo.id;
    const alvos = new Set<string>();
    const noCaminho = new Set<string>([raizId]);
    const filhosNoCaminho: Record<string, string[]> = {};
    const membros: Record<string, string[]> = { [raizId]: lojas };
    arv.resultadoBusca.alvos.forEach((id) => {
      let atual = raizId;
      caminhoDe(arv.arvore!, id)
        .slice(1) // pula a loja: já é o `membros` da raiz
        .forEach((realId) => {
          const no = nos[realId];
          if (!no) return;
          const proximo = `${atual}/${no.tipoNodo}:${no.nome}`;
          if (!noCaminho.has(proximo)) {
            noCaminho.add(proximo);
            (filhosNoCaminho[atual] ??= []).push(proximo);
          }
          const membrosDoNo = (membros[proximo] ??= []);
          if (!membrosDoNo.includes(realId)) membrosDoNo.push(realId);
          atual = proximo;
        });
      alvos.add(atual);
    });
    Object.keys(filhosNoCaminho).forEach((pai) => {
      filhosNoCaminho[pai]!.sort((a, b) =>
        (nos[membros[a]?.[0] ?? ""]?.nome ?? "").localeCompare(nos[membros[b]?.[0] ?? ""]?.nome ?? ""),
      );
    });
    return { alvos, noCaminho, filhosNoCaminho, membros };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arv.arvore, arv.resultadoBusca, lojas]);

  /** Monta o `NoGrupo` de um id do caminho de `focoGrupo` a partir dos `membros` que ele juntou. */
  const grupoDoFoco = (id: string, nivel: number): NoGrupo | undefined => {
    const membrosDoNo = focoGrupo?.membros[id];
    const exemplo = membrosDoNo?.length ? nos[membrosDoNo[0]!] : undefined;
    if (!exemplo || !membrosDoNo) return undefined;
    return { id, nome: exemplo.nome, tipoNodo: exemplo.tipoNodo, nivel, membros: membrosDoNo };
  };

  const linhas: React.ReactNode[] = [];
  // Uma linha por nó exibido, na mesma ordem e com os mesmos valores da
  // tabela — é o que "Exportar Excel" grava, sem refazer a busca/filtros.
  const linhasExportaveis: LinhaExportavel[] = [];
  const visitar = (no: NoMeta, dentroDeAlvo: boolean) => {
    // A tela termina na última categoria: produtos não viram linha.
    if (no.tipoNodo === "PRODUTO") return;
    if (foco && !dentroDeAlvo && !foco.noCaminho.has(no.id)) return;
    const dentro = dentroDeAlvo || !!foco?.alvos.has(no.id);
    const travado = !!foco && !dentro;
    const filhosCarregados = arv.arvore?.filhos[no.id];
    const aberto = travado || arv.expandidos.has(no.id);
    const filhosVisiveis = travado ? (foco.filhosNoCaminho[no.id] ?? []) : aberto ? (filhosCarregados ?? []) : [];
    // Folha = último nível de categoria do cliente, ou categoria cujos filhos
    // carregados são só produtos (mesma regra do flash).
    const expansivel =
      no.nivel < arv.profundidadeMaxima &&
      !(filhosCarregados && filhosCarregados.every((id) => nos[id]?.tipoNodo === "PRODUTO"));

    const valorAjustado = ajustada(no.id);
    const calculada = no.calculado;
    const contribuicao = no.contribuicaoCalculada;
    // Um item por nó pra `percentualContribuicao`: o próprio nó, com seu
    // PERC_CONTRIBUICAO_ENVIADO (ver metasFormato.ts).
    const itensContribuicao: ItemContribuicao[] = [
      { percEnviado: no.percContribuicaoEnviado, contribuicao: no.contribuicaoCalculada, calculada: no.calculado },
    ];
    const historico = no.historico;
    const paiOriginal = no.paiId ? calculadaDe(no.paiId) : totalOriginalSelecionado;
    const paiAjustado = no.paiId ? ajustada(no.paiId) : totalSelecionado;
    const ajusteProprio = ajustes[no.id] !== undefined;

    linhasExportaveis.push(
      linhaExportavel(
        `${"  ".repeat(no.nivel)}${rotuloNivel(no)} ${no.nome}`.trim(),
        historico,
        calculada,
        paiOriginal,
        valorAjustado,
        paiAjustado,
        contribuicao,
        itensContribuicao,
      ),
    );

    linhas.push(
      <LinhaMeta
        key={no.id}
        id={no.id}
        nivel={no.nivel}
        rotulo={rotuloNivel(no)}
        nome={no.nome}
        historico={historico}
        calculada={calculada}
        ajustada={valorAjustado}
        contribuicao={contribuicao}
        percentualContribuicao={percentualContribuicao(itensContribuicao)}
        paiOriginal={paiOriginal}
        paiAjustado={paiAjustado}
        expansivel={expansivel}
        aberto={aberto}
        carregando={arv.carregando.has(no.id)}
        onToggle={() => alternarExpandido(no.id, travado)}
        editavel={!somenteLeitura}
        ajusteProprio={ajusteProprio}
        onEditarValor={() => abrirEdicao(no, "valor")}
        onEditarPercentual={() => abrirEdicao(no, "percentual")}
        onHistorico={() => abrirHistorico(no)}
      />,
    );

    filhosVisiveis.forEach((id) => {
      const filho = nos[id];
      if (filho) visitar(filho, dentro);
    });
  };

  /**
   * Visão Grupo: mesma ideia de `visitar`, mas sobre `NoGrupo` e com o foco de
   * busca vindo de `focoGrupo` (não de `foco`, que é em ids reais) — sem
   * "somente alteradas", que não faz sentido consolidado — e sempre somente
   * leitura. `calculada`/`valorAjustado` (os valores do próprio `grupo`) e
   * `paiOriginal`/`paiAjustado` vêm por parâmetro: quem chama já precisou
   * computar `calculada`/`valorAjustado` pra decidir os parâmetros da
   * recursão nos filhos, então recebê-los prontos evita somar de novo os
   * mesmos `membros` aqui dentro. Um `NoGrupo` também não tem `paiId` fixo (o
   * mesmo nome pode agrupar membros com pais diferentes entre lojas), daí
   * `paiOriginal`/`paiAjustado` também virem por fora.
   */
  const visitarGrupo = (
    grupo: NoGrupo,
    calculada: number,
    valorAjustado: number,
    contribuicao: number,
    paiOriginal: number,
    paiAjustado: number,
    dentroDeAlvo: boolean,
  ) => {
    if (grupo.tipoNodo === "PRODUTO") return;
    if (focoGrupo && !dentroDeAlvo && !focoGrupo.noCaminho.has(grupo.id)) return;
    const dentro = dentroDeAlvo || !!focoGrupo?.alvos.has(grupo.id);
    const travado = !!focoGrupo && !dentro;

    // Um item por membro do grupo (cada um com seu próprio
    // PERC_CONTRIBUICAO_ENVIADO), pra média ponderada não perder o percentual
    // de cada nó.
    const itensContribuicao: ItemContribuicao[] = visaoGrupo.itensContribuicaoGrupo(grupo);
    const historico = visaoGrupo.historicoGrupo(grupo);
    const aberto = travado || visaoGrupo.expandidosGrupo.has(grupo.id);
    const filhosTodos = visaoGrupo.filhosDeGrupo(grupo).filter((g) => g.tipoNodo !== "PRODUTO");
    const filhosVisiveis = travado
      ? (focoGrupo!.filhosNoCaminho[grupo.id] ?? [])
          .map((id) => grupoDoFoco(id, grupo.nivel + 1))
          .filter((g): g is NoGrupo => !!g)
      : aberto
        ? filhosTodos
        : [];
    const todosCarregados = grupo.membros.every((id) => arv.arvore?.filhos[id]);
    const expansivel = grupo.nivel < arv.profundidadeMaxima && !(todosCarregados && filhosTodos.length === 0);
    // "Exibir até" na visão Grupo esconde os níveis acima do escolhido
    // (inclusive a raiz GRUPO, se o nível pedido for maior que zero): eles
    // continuam percorridos (pra revelar o nível pedido), só não viram linha.
    // Diferente da visão Por loja, aqui vale para qualquer nível selecionado.
    // A partir do N3 os níveis intermediários (N2 em diante) aparecem junto, pra dar contexto.
    const nivelMinimoVisivel = profundidadeGrupo >= 3 ? 2 : profundidadeGrupo;
    const ocultoPeloFiltro = grupo.nivel < nivelMinimoVisivel;

    if (!ocultoPeloFiltro) {
      linhasExportaveis.push(
        linhaExportavel(
          `${"  ".repeat(grupo.nivel)}${visaoGrupo.rotuloNivelGrupo(grupo)} ${grupo.nome}`.trim(),
          historico,
          calculada,
          paiOriginal,
          valorAjustado,
          paiAjustado,
          contribuicao,
          itensContribuicao,
        ),
      );

      linhas.push(
        <LinhaMeta
          key={grupo.id}
          id={grupo.id}
          nivel={grupo.nivel}
          rotulo={visaoGrupo.rotuloNivelGrupo(grupo)}
          nome={grupo.nome}
          historico={historico}
          calculada={calculada}
          ajustada={valorAjustado}
          percentualContribuicao={percentualContribuicao(itensContribuicao)}
          contribuicao={contribuicao}
          paiOriginal={paiOriginal}
          paiAjustado={paiAjustado}
          expansivel={expansivel}
          aberto={aberto}
          carregando={visaoGrupo.carregandoGrupo(grupo)}
          onToggle={() => {
            // Mesma trava da visão Por loja: um ramo aberto pela busca não
            // pode ser recolhido, senão esconderia o que ela trouxe.
            if (travado) {
              notify("Limpe a busca para recolher a árvore.", "info");
              return;
            }
            visaoGrupo.alternarExpandidoGrupo(grupo);
          }}
          editavel={!somenteLeitura}
          editavelPercentual={!somenteLeitura && grupo.nivel !== 0}
          ajusteProprio={false}
          onEditarValor={() => abrirEdicaoGrupo(grupo, "valor")}
          onEditarPercentual={() => abrirEdicaoGrupo(grupo, "participacao")}
        />,
      );
    }

    filhosVisiveis.forEach((filho) =>
      visitarGrupo(
        filho,
        visaoGrupo.calculadaGrupo(filho),
        visaoGrupo.ajustadaGrupo(filho),
        visaoGrupo.contribuicaoGrupo(filho),
        calculada,
        valorAjustado,
        dentro,
      ),
    );
  };

  if (arv.arvore) {
    if (verPor === "grupo") {
      const calculadaRaiz = visaoGrupo.calculadaGrupo(visaoGrupo.raizGrupo);
      const ajustadaRaiz = visaoGrupo.ajustadaGrupo(visaoGrupo.raizGrupo);
      const contribuicaoRaiz = visaoGrupo.contribuicaoGrupo(visaoGrupo.raizGrupo);
      visitarGrupo(
        visaoGrupo.raizGrupo,
        calculadaRaiz,
        ajustadaRaiz,
        contribuicaoRaiz,
        calculadaRaiz,
        ajustadaRaiz,
        !focoGrupo,
      );
    } else {
      lojasSelecionadas.forEach((id) => {
        const loja = nos[id];
        if (loja) visitar(loja, !foco);
      });
    }
  }
  if (lojasSelecionadas.length > 0) {
    linhasExportaveis.push({
      rotulo: "TOTAL",
      historico: historicoTotalSelecionado,
      calculada: totalOriginalSelecionado,
      participacaoOriginal: 1,
      ajustada: totalSelecionado,
      participacaoContribuicao: 1,
      diferenca: contribuicaoTotalSelecionada,
      variacao: fracaoContribuicao(itensContribuicaoTotal),
    });
  }

  /**
   * Só o que está na tela agora — mesmos filtros, busca e níveis expandidos
   * (`linhasExportaveis`, montada junto de `visitar`/`visitarGrupo`/TOTAL).
   * Usa `exportTableToExcel` (DataTable.tsx) com número de verdade em cada
   * célula (não texto já formatado como "R$ 1.234"): senão SUM, ordenação e
   * formatação condicional não funcionam sobre as colunas de valor/percentual
   * no Excel. A formatação de cada coluna fica no `excelFormat`.
   */
  const exportarExcel = () => {
    if (!cenario) return;
    const meses = mesesAnteriores(cenario.anoMeta, cenario.mesMeta);
    const FORMATO_MOEDA = '"R$" #,##0';
    const FORMATO_PERCENTUAL = "0.00%";
    const colunas: Column<LinhaExportavel>[] = [
      {
        key: "rotulo",
        label: verPor === "grupo" ? "GRUPO / CATEGORIA" : "LOJA / CATEGORIA",
        exportValue: (linha) => linha.rotulo,
      },
      ...meses.map((m, i) => ({
        key: `mes-${i}`,
        label: rotuloMes(m.ano, m.mes),
        exportValue: (linha: LinhaExportavel) => linha.historico[i] ?? 0,
        excelFormat: FORMATO_MOEDA,
      })),
      { key: "calculada", label: "VALOR ORIGINAL", exportValue: (linha) => linha.calculada, excelFormat: FORMATO_MOEDA },
      {
        key: "participacaoOriginal",
        label: "% ORIGINAL",
        exportValue: (linha) => linha.participacaoOriginal,
        excelFormat: FORMATO_PERCENTUAL,
      },
      { key: "ajustada", label: "VALOR INFORMADO", exportValue: (linha) => linha.ajustada, excelFormat: FORMATO_MOEDA },
      {
        key: "participacaoContribuicao",
        label: "% PARTICIPAÇÃO",
        exportValue: (linha) => linha.participacaoContribuicao,
        excelFormat: FORMATO_PERCENTUAL,
      },
      { key: "diferenca", label: "VALOR CONTRIBUIÇÃO", exportValue: (linha) => linha.diferenca, excelFormat: FORMATO_MOEDA },
      {
        key: "variacao",
        label: "% CONTRIBUIÇÃO",
        exportValue: (linha) => linha.variacao,
        excelFormat: FORMATO_PERCENTUAL,
      },
    ];
    const nomeArquivo = `metas-${normalizar(rotuloMes(cenario.anoMeta, cenario.mesMeta))}`
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    exportTableToExcel(colunas, linhasExportaveis, nomeArquivo, "Metas");
  };

  /**
   * Relatório comercial: meta por loja × departamento (N1) × seção (N2), no
   * formato do `comercial_*.csv` do cliente (sem o realizado). Independe do
   * que está aberto na tela: carrega até o N2 de todas as lojas (cache em
   * `carregarNiveis`) e usa as lojas do filtro, com os ajustes ainda não
   * salvos — o mesmo valor que a tela mostra.
   */
  const exportarRelatorioComercial = async () => {
    if (!podeExportarRelatorios || !cenario) return;
    setExportandoComercial(true);
    try {
      const arvoreCompleta = await arv.carregarNiveis(2);
      if (!arvoreCompleta) return;
      const linhasComercial = linhasRelatorioComercial(
        arvoreCompleta,
        ajustes,
        lojasSelecionadas,
        cenario.anoMeta,
        cenario.mesMeta,
      );
      const FORMATO_MOEDA = "#,##0.00";
      const colunas: Column<LinhaRelatorioComercial>[] = [
        { key: "mesAno", label: "Mes ano numeral", exportValue: (l) => l.mesAno },
        { key: "loja", label: "Loja", exportValue: (l) => l.loja },
        { key: "departamento", label: "Departamento Nv1", exportValue: (l) => l.departamento },
        { key: "secao", label: "Seção Nv2", exportValue: (l) => l.secao },
        { key: "metaFaturamento", label: "Vlr Meta Faturamento", exportValue: (l) => l.metaFaturamento, excelFormat: FORMATO_MOEDA },
        { key: "metaContribuicao", label: "Vlr Meta Contribuição", exportValue: (l) => l.metaContribuicao, excelFormat: FORMATO_MOEDA },
        { key: "metaPerda", label: "Vlr Meta Perda", exportValue: (l) => l.metaPerda, excelFormat: FORMATO_MOEDA },
      ];
      const nomeArquivo = `comercial-${cenario.anoMeta}${String(cenario.mesMeta).padStart(2, "0")}`;
      exportTableToExcel(colunas, linhasComercial, nomeArquivo, "Comercial");
    } finally {
      setExportandoComercial(false);
    }
  };

  /**
   * Relatório por comprador: N1 × N2 × N3 × comprador somado entre lojas, no
   * formato do `comprador_*.csv` do cliente (sem o realizado). Os valores vêm
   * da soma dos produtos no motor; os ajustes entram aproximados, pela razão
   * ajustada ÷ calculada do N3 de cada loja (ver `linhasRelatorioComprador`).
   */
  const exportarRelatorioComprador = async () => {
    if (!podeExportarRelatorios || !cenario || !cenario.idCalculoCliente) return;
    setExportandoComprador(true);
    try {
      const codigosLojas = lojasSelecionadas
        .map((id) => nos[id]?.codEmpresa)
        .filter((cod): cod is number => cod !== null && cod !== undefined);
      // Uma chamada por loja, em lotes: a query soma todos os produtos do
      // cálculo e numa chamada só estoura o limite de 30s da API.
      const idCalculo = cenario.idCalculoCliente;
      const calculadas: Awaited<ReturnType<typeof listRelatorioComprador>> = [];
      const carregarLojas = (async () => {
        for (let i = 0; i < codigosLojas.length; i += LOJAS_POR_LOTE_COMPRADOR) {
          const lote = codigosLojas.slice(i, i + LOJAS_POR_LOTE_COMPRADOR);
          const partes = await Promise.all(lote.map((cod) => listRelatorioComprador(clientId, idCalculo, cod)));
          partes.forEach((parte) => calculadas.push(...parte));
        }
      })();
      const [arvoreN3] = await Promise.all([arv.carregarNiveis(3), carregarLojas]);
      if (!arvoreN3) return;
      if (calculadas.length === 0) {
        notify("O cálculo não tem metas por produto — o relatório por comprador precisa de um cenário que inclua produto.", "warning");
        return;
      }
      const linhasComprador = linhasRelatorioComprador(
        calculadas,
        arvoreN3,
        ajustes,
        codigosLojas,
        cenario.anoMeta,
        cenario.mesMeta,
      );
      const FORMATO_MOEDA = "#,##0.00";
      const colunas: Column<LinhaRelatorioComprador>[] = [
        { key: "mesAno", label: "Mes ano numeral", exportValue: (l) => l.mesAno },
        { key: "departamento", label: "Departamento Mix (Nv_1)", exportValue: (l) => l.departamento },
        { key: "secao", label: "Secao Mix (Nv_2)", exportValue: (l) => l.secao },
        { key: "subsecao", label: "Sub Secao Mix (Nv_3)", exportValue: (l) => l.subsecao },
        { key: "comprador", label: "Comprador", exportValue: (l) => l.comprador },
        { key: "metaFaturamento", label: "Vlr Meta Faturamento", exportValue: (l) => l.metaFaturamento, excelFormat: FORMATO_MOEDA },
        { key: "metaContribuicao", label: "Valor da meta de contribuição", exportValue: (l) => l.metaContribuicao, excelFormat: FORMATO_MOEDA },
        { key: "metaPerda", label: "Vlr Meta Perda", exportValue: (l) => l.metaPerda, excelFormat: FORMATO_MOEDA },
      ];
      const nomeArquivo = `comprador-${cenario.anoMeta}${String(cenario.mesMeta).padStart(2, "0")}`;
      exportTableToExcel(colunas, linhasComprador, nomeArquivo, "Comprador");
    } catch {
      notify("Não foi possível gerar o relatório por comprador.", "error");
    } finally {
      setExportandoComprador(false);
    }
  };

  /**
   * Relatório N4: categoria (N2) × N3 × N4 consolidado entre as lojas, uma
   * aba por categoria, como o `Set_Nivel4.xlsx` do cliente — só as colunas
   * que o módulo tem (sem estoque, compras nem realizado). Carregar o N4 de
   * todas as lojas é pesado: vem em pedaços (`carregarAteN4`), com o progresso
   * no botão, e o que já veio fica na árvore para a próxima exportação.
   */
  const exportarRelatorioN4 = async () => {
    if (!podeExportarRelatorios || !cenario) return;
    setProgressoN4({ feitos: 0, total: 0 });
    try {
      const arvoreN4 = await arv.carregarAteN4(lojasSelecionadas, (feitos, total) => setProgressoN4({ feitos, total }));
      if (!arvoreN4) return;
      const periodo = rotuloMes(cenario.anoMeta, cenario.mesMeta);
      const linhasN4 = linhasRelatorioN4(arvoreN4, ajustes, lojasSelecionadas, periodo);
      if (linhasN4.length === 0) {
        // Diagnóstico: quantos nós vieram em cada profundidade (loja = 0) e de que tipo —
        // mostra se o N4 não existe na árvore do cliente ou se a carga não o trouxe.
        const contagem: Record<string, number> = {};
        Object.values(arvoreN4.nos).forEach((no) => {
          const chave = `${no.tipoNodo === "PRODUTO" ? "produto" : "N" + no.nivel}@${no.nivel}`;
          contagem[chave] = (contagem[chave] ?? 0) + 1;
        });
        console.info("Relatório N4 vazio — nós carregados por tipo@profundidade:", contagem);
        const resumo = Object.entries(contagem)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([chave, qtd]) => `${chave}: ${qtd}`)
          .join(" · ");
        notify(`Não há metas no nível N4 para as lojas selecionadas. Carregado — ${resumo}`, "warning");
        return;
      }
      const meses = mesesAnteriores(cenario.anoMeta, cenario.mesMeta);
      const anterior = meses[meses.length - 1];
      const abreviado = (ano: number, mes: number) =>
        `${new Date(ano, mes - 1, 1).toLocaleDateString("pt-BR", { month: "short" }).replace(".", "")}/${String(ano).slice(2)}`;
      const nomeMes = new Date(cenario.anoMeta, cenario.mesMeta - 1, 1).toLocaleDateString("pt-BR", { month: "long" }).toUpperCase();
      // Uma aba por categoria (N2) — cada uma com os itens e o TOTAL, que
      // `linhasRelatorioN4` já entrega juntos.
      const abas = new Map<string, LinhaRelatorioN4[]>();
      linhasN4.forEach((linha) => abas.set(linha.categoria, [...(abas.get(linha.categoria) ?? []), linha]));
      exportarRelatorioN4Estilizado(abas, {
        titulo: `${nomeMes} ${cenario.anoMeta.toLocaleString("pt-BR")}`,
        rotuloAnterior: anterior ? abreviado(anterior.ano, anterior.mes) : "mês anterior",
        rotuloMeta: abreviado(cenario.anoMeta, cenario.mesMeta),
        nomeArquivo: `nivel4-${cenario.anoMeta}${String(cenario.mesMeta).padStart(2, "0")}`,
      });
    } catch {
      notify("Não foi possível gerar o relatório N4.", "error");
    } finally {
      setProgressoN4(null);
    }
  };

  const alvo = alvoEdicao();
  const alvoValido = Number.isSafeInteger(alvo) && alvo >= 0 && alvo <= META_MAXIMA;

  if (loadingCenarios) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (cenarios.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Nenhum cenário cadastrado
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Crie um cenário na aba Período para poder calculá-lo e revisar as metas aqui.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack gap={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={2}
      >
        <Typography variant="body2" color="text.secondary">
          Revise as metas calculadas, ajuste os valores e acompanhe os totais.
        </Typography>
        <Stack direction="row" gap={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            disabled={somenteLeitura || !sujo || processando !== null}
            startIcon={processando === "salvar" ? <CircularProgress size={14} /> : undefined}
            onClick={salvarAjustes}
          >
            {processando === "salvar" ? "Salvando..." : "Salvar ajustes"}
          </Button>
          <Button
            variant="contained"
            disabled={somenteLeitura || processando !== null}
            startIcon={processando === "publicar" || promovendoId ? <CircularProgress size={14} /> : undefined}
            onClick={pedirPublicar}
          >
            {processando === "publicar" || promovendoId ? "Publicando..." : cenario?.status === "PROMOVIDO" ? "Republicar" : "Publicar"}
          </Button>
        </Stack>
      </Stack>

      <Stack gap={2}>
        <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems={{ sm: "center" }}>
          <TextField
            select
            label="Cenário para cálculo"
            size="small"
            fullWidth
            value={cenarioId}
            onChange={(e) => pedirTrocarCenario(e.target.value)}
            sx={{ maxWidth: { sm: 460 } }}
          >
            {cenarios.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.descricao} — {situacaoDe(statusCalculoDe(c.status ?? "RASCUNHO"), true).toLowerCase()}
              </MenuItem>
            ))}
          </TextField>
          <Stack gap={0.5}>
            <Chip
              size="small"
              label={cenario ? situacaoDe(statusCalculoDe(cenario.status ?? "RASCUNHO"), true) : "—"}
              color={cenario ? corSituacao(statusCalculoDe(cenario.status ?? "RASCUNHO")) : "default"}
              variant="outlined"
              sx={{ alignSelf: "flex-start" }}
            />
            <Typography variant="caption" color="text.secondary">
              {sujo ? "Há ajustes não salvos." : "Ajustes salvos no último Salvar ajustes."}
            </Typography>
          </Stack>
        </Stack>

        <Alert
          severity={falhaTransitoria ? "warning" : cenario?.status === "PROMOVIDO" && !negadoSemFluxo ? "warning" : "info"}
          sx={{ mt: 2 }}
          action={
            falhaTransitoria ? (
              <Button color="inherit" size="small" onClick={fluxo.recarregar}>
                Tentar novamente
              </Button>
            ) : undefined
          }
        >
          {falhaTransitoria
            ? "Não foi possível verificar suas permissões neste cenário (falha de comunicação). Os ajustes ficam bloqueados até a verificação funcionar."
            : bloqueadoPeloFluxo && !fluxo.estado?.ciclo && cenario?.status === "CALCULADO"
            ? "Fluxo de aprovação ativo e nenhum ciclo aberto para este cenário: inicie o ciclo na aba Aprovações para liberar os ajustes a quem calcula."
            : bloqueadoPeloFluxo
            ? "Fluxo de aprovação ativo: os valores só podem ser alterados por quem calcula, enquanto o próprio escopo está em cálculo. Sugestões vão nas observações da aba Aprovações, onde também ficam a revisão, a aprovação e a publicação."
            : negadoSemFluxo
            ? "Somente leitura: ajustar e publicar as metas exige o papel \"Criador de meta\", concedido no cadastro do usuário."
            : cenario?.status === "PROMOVIDO"
            ? "Cenário publicado. Os ajustes podem ser alterados e o cenário republicado — as metas já publicadas serão atualizadas."
            : cenario?.status === "PROMOVENDO"
              ? "Publicando o cenário. Aguarde terminar — as metas ficam somente leitura até lá."
              : somenteLeitura
              ? "Aguardando o cálculo das metas."
              : "Edição liberada. Ajustes ficam salvos assim que \"Salvar ajustes\" é usado."}
        </Alert>
      </Stack>

      {arv.carregandoRevisao ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
          <CircularProgress />
        </Box>
      ) : arv.erroRevisao ? (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Não foi possível carregar a revisão
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Tente selecionar o cenário novamente.
          </Typography>
        </Box>
      ) : !arv.arvore ? (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Este cenário ainda não foi calculado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            As metas estarão disponíveis para revisão após o cálculo do cenário.
          </Typography>
        </Box>
      ) : (
        <>
          <Stack gap={2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.3fr 1fr 1fr" },
                gap: 2,
                mb: 2,
              }}
            >
              <Kpi
                destaque
                titulo="Meta global do cenário"
                valor={moeda(global)}
                nota="Todas as lojas, independente dos filtros"
              />
              <Kpi
                titulo="Meta das lojas selecionadas"
                valor={moeda(totalSelecionado)}
                nota={lojaFiltro === "todas" ? "Todas as lojas" : (nos[lojaFiltro]?.nome ?? "")}
              />
              <Kpi
                titulo="Ajuste sobre o cálculo original"
                valor={moeda(global - calculadoTotal)}
                nota={`${variacaoPercentual(global, calculadoTotal)} em relação à meta calculada`}
              />
            </Box>
          </Stack>

          <Stack gap={2}>
            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
              <Box sx={{ p: { xs: 2, sm: 2.5 }, borderBottom: "1px solid", borderColor: "divider" }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1} sx={{ mb: 2 }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={verPor}
                    onChange={(_, novo: "loja" | "grupo" | null) => novo && void trocarVerPor(novo)}
                  >
                    <ToggleButton value="grupo">Grupo (todas as lojas)</ToggleButton>
                    <ToggleButton value="loja">Por loja</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>

                <Stack direction={{ xs: "column", md: "row" }} gap={2}>
                  {verPor === "loja" && (
                    <TextField
                      select
                      label="Loja"
                      size="small"
                      fullWidth
                      value={lojaFiltro}
                      onChange={(e) => trocarLojaFiltro(e.target.value)}
                    >
                      <MenuItem value="todas">Todas as lojas</MenuItem>
                      {lojas.map((id) => (
                        <MenuItem key={id} value={id}>
                          {nos[id]?.nome}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <TextField
                    label="Buscar categoria"
                    size="small"
                    fullWidth
                    sx={verPor === "grupo" ? { maxWidth: { md: 600 } } : undefined}
                    value={arv.busca}
                    onChange={(e) => arv.setBusca(e.target.value)}
                    placeholder="Nome ou código da categoria"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: arv.buscando ? (
                          <InputAdornment position="end">
                            <CircularProgress size={16} aria-label="Buscando" />
                          </InputAdornment>
                        ) : undefined,
                      },
                    }}
                  />
                  <TextField
                    select
                    label="Exibir até"
                    size="small"
                    fullWidth
                    disabled={arv.carregandoProfundidade}
                    value={String(profundidadeAtual)}
                    onChange={(e) => void mudarProfundidade(Number(e.target.value))}
                    sx={{ maxWidth: { md: 220 } }}
                  >
                    <MenuItem value="0">Loja</MenuItem>
                    {Array.from({ length: arv.profundidadeMaxima }, (_, i) => i + 1).map((nivel) => (
                      <MenuItem key={nivel} value={String(nivel)}>
                        Categoria N{nivel}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ sm: "center" }}
                  gap={1}
                  sx={{ mt: 1 }}
                >
                  {verPor === "loja" ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={somenteAlteradas}
                          onChange={(e) => setSomenteAlteradas(e.target.checked)}
                        />
                      }
                      label="Somente metas alteradas"
                    />
                  ) : (
                    <Box />
                  )}
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    <Button size="small" onClick={recolherTudo}>
                      Recolher tudo
                    </Button>
                    <Button size="small" onClick={limparFiltros}>
                      Limpar filtros
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<FileDownload fontSize="small" />}
                      endIcon={<KeyboardArrowDown fontSize="small" />}
                      disabled={linhasExportaveis.length === 0 && lojasSelecionadas.length === 0}
                      onClick={(e) => setMenuExportar(e.currentTarget)}
                    >
                      {progressoN4
                        ? progressoN4.total
                          ? `Carregando N4... ${progressoN4.feitos}/${progressoN4.total}`
                          : "Gerando..."
                        : exportandoComercial || exportandoComprador
                          ? "Gerando..."
                          : "Exportar"}
                    </Button>
                    <Menu anchorEl={menuExportar} open={!!menuExportar} onClose={() => setMenuExportar(null)}>
                      <MenuItem
                        disabled={linhasExportaveis.length === 0}
                        onClick={() => {
                          setMenuExportar(null);
                          exportarExcel();
                        }}
                      >
                        <ListItemText primary="Planilha atual" secondary="Linhas visíveis, como na tela" />
                      </MenuItem>
                      {podeExportarRelatorios && (
                        <MenuItem
                          disabled={lojasSelecionadas.length === 0 || exportandoComercial}
                          onClick={() => {
                            setMenuExportar(null);
                            void exportarRelatorioComercial();
                          }}
                        >
                          <ListItemText primary="Relatório comercial" />
                        </MenuItem>
                      )}
                      {podeExportarRelatorios && (
                        <MenuItem
                          disabled={lojasSelecionadas.length === 0 || exportandoComprador}
                          onClick={() => {
                            setMenuExportar(null);
                            void exportarRelatorioComprador();
                          }}
                        >
                          <ListItemText primary="Relatório por comprador" />
                        </MenuItem>
                      )}
                      {podeExportarRelatorios && (
                        <MenuItem
                          disabled={lojasSelecionadas.length === 0 || progressoN4 !== null}
                          onClick={() => {
                            setMenuExportar(null);
                            void exportarRelatorioN4();
                          }}
                        >
                          <ListItemText primary="Relatório N4" />
                        </MenuItem>
                      )}
                    </Menu>
                  </Stack>
                </Stack>
              </Box>

              <Box ref={wrapperTabelaRef} sx={{ overflow: "auto", maxHeight: ALTURA_TABELA }}>
                <Table
                  size="small"
                  stickyHeader
                  sx={{ tableLayout: "fixed", width: LARGURAS_COLUNAS.reduce((acc, largura) => acc + largura, 0) }}
                >
                  <colgroup>
                    {LARGURAS_COLUNAS.map((largura, i) => (
                      <col key={i} style={{ width: largura }} />
                    ))}
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        rowSpan={2}
                        sx={{
                          whiteSpace: "nowrap",
                          height: ALTURA_LINHA_CABECALHO * 2,
                          top: 0,
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                        }}
                      >
                        {verPor === "grupo" ? "GRUPO / CATEGORIA" : "LOJA / CATEGORIA"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ whiteSpace: "nowrap", height: ALTURA_LINHA_CABECALHO, top: 0, width: LARGURA_JANELA_MESES }}
                      >
                        MESES ANTERIORES
                      </TableCell>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{ whiteSpace: "nowrap", height: ALTURA_LINHA_CABECALHO, top: 0 }}
                      >
                        {cenario ? rotuloMes(cenario.anoMeta, cenario.mesMeta).toUpperCase() : "MÊS ATUAL"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{ p: 0, height: ALTURA_LINHA_CABECALHO, top: ALTURA_LINHA_CABECALHO, position: "sticky" }}
                      >
                        <Box
                          ref={mesesCabecalhoRef}
                          onScroll={aoRolarMeses}
                          sx={{ overflowX: "auto", width: LARGURA_JANELA_MESES, height: "100%" }}
                        >
                          <Stack direction="row" sx={{ height: "100%" }}>
                            {(cenario ? mesesAnteriores(cenario.anoMeta, cenario.mesMeta) : []).map((m, i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: LARGURA_COLUNA_MES,
                                  flexShrink: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  whiteSpace: "nowrap",
                                  px: 2,
                                }}
                              >
                                {rotuloMes(m.ano, m.mes)}
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: "nowrap", minWidth: 150, height: ALTURA_LINHA_CABECALHO, top: ALTURA_LINHA_CABECALHO }}
                      >
                        VALOR ORIGINAL
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: "nowrap", minWidth: 110, height: ALTURA_LINHA_CABECALHO, top: ALTURA_LINHA_CABECALHO }}
                      >
                        % ORIGINAL
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: "nowrap", minWidth: 150, height: ALTURA_LINHA_CABECALHO, top: ALTURA_LINHA_CABECALHO }}
                      >
                        VALOR INFORMADO
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: "nowrap", minWidth: 130, height: ALTURA_LINHA_CABECALHO, top: ALTURA_LINHA_CABECALHO }}
                      >
                        % PARTICIPAÇÃO
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: "nowrap", minWidth: 160, height: ALTURA_LINHA_CABECALHO, top: ALTURA_LINHA_CABECALHO }}
                      >
                        VALOR CONTRIBUIÇÃO
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: "nowrap", minWidth: 130, height: ALTURA_LINHA_CABECALHO, top: ALTURA_LINHA_CABECALHO }}
                      >
                        % CONTRIBUIÇÃO
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {linhas.length ? (
                      <LinhasVirtualizadas
                        linhas={linhas}
                        containerRef={wrapperTabelaRef}
                        alturaCabecalho={ALTURA_LINHA_CABECALHO * 2}
                        colunas={LARGURAS_COLUNAS.length}
                      />
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                          Nenhuma meta corresponde aos filtros.
                        </TableCell>
                      </TableRow>
                    )}
                    {lojasSelecionadas.length > 0 && (
                      // `position: sticky` vai em cada `td`, não na `tr` — mesma razão do
                      // cabeçalho: a linha em si permanece no fluxo normal e rola para fora,
                      // então cada célula gruda em `bottom: 0` por conta própria e precisa do
                      // próprio `bgcolor` (célula não herda o background da `tr` quando
                      // destacada do fluxo pelo sticky).
                      <TableRow sx={{ "& td": { fontWeight: 800 } }}>
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            bottom: 0,
                            zIndex: 3,
                            bgcolor: "background.subtle",
                          }}
                        >
                          TOTAL
                        </TableCell>
                        <TableCell sx={{ p: 0, width: LARGURA_JANELA_MESES, position: "sticky", bottom: 0, zIndex: 2, bgcolor: "background.subtle" }}>
                          <Box sx={{ overflow: "hidden", width: LARGURA_JANELA_MESES }}>
                            <Stack direction="row" sx={{ transform: "translateX(var(--meses-scroll, 0px))", willChange: "transform" }}>
                              {historicoTotalSelecionado.map((venda, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    width: LARGURA_COLUNA_MES,
                                    flexShrink: 0,
                                    textAlign: "right",
                                    px: 2,
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {moeda(venda)}
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ position: "sticky", bottom: 0, zIndex: 2, bgcolor: "background.subtle" }}>
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
                            {moeda(totalOriginalSelecionado)}
                            {!somenteLeitura && (
                              <Box sx={{ width: LARGURA_ICONE_EDICAO, height: LARGURA_ICONE_EDICAO }} />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ position: "sticky", bottom: 0, zIndex: 2, bgcolor: "background.subtle" }}>
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
                            100%
                            {!somenteLeitura && (
                              <Box sx={{ width: LARGURA_ICONE_EDICAO, height: LARGURA_ICONE_EDICAO }} />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ position: "sticky", bottom: 0, zIndex: 2, bgcolor: "background.subtle" }}>
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
                            {moeda(totalSelecionado)}
                            {!somenteLeitura && (
                              <Box sx={{ width: LARGURA_ICONE_EDICAO, height: LARGURA_ICONE_EDICAO }} />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ position: "sticky", bottom: 0, zIndex: 2, bgcolor: "background.subtle" }}>
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
                            100%
                            {!somenteLeitura && (
                              <Box sx={{ width: LARGURA_ICONE_EDICAO, height: LARGURA_ICONE_EDICAO }} />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            position: "sticky",
                            bottom: 0,
                            zIndex: 2,
                            bgcolor: "background.subtle",
                            color: contribuicaoTotalSelecionada < 0 ? "error.main" : "inherit",
                          }}
                        >
                          {moeda(contribuicaoTotalSelecionada)}
                        </TableCell>
                        <TableCell align="right" sx={{ position: "sticky", bottom: 0, zIndex: 2, bgcolor: "background.subtle" }}>
                          {percentualContribuicao(itensContribuicaoTotal)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                gap={1}
                sx={{ p: 2, bgcolor: "background.subtle" }}
              >
                <Typography variant="caption" color="text.secondary">
                  {linhas.length} linhas exibidas · {lojas.length} lojas no cenário
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </>
      )}

      {/* "Exibir até" pode buscar vários níveis de todas as lojas: enquanto isso a tela inteira fica travada. */}
      <Backdrop open={arv.carregandoProfundidade} sx={{ zIndex: (theme) => theme.zIndex.modal + 1, bgcolor: "transparent" }}>
        <CircularProgress aria-label="Carregando níveis" />
      </Backdrop>

      <Dialog open={!!editando} onClose={() => setEditando(null)} fullWidth maxWidth="sm">
        <DialogTitle>Ajustar meta</DialogTitle>
        <DialogContent dividers>
          {editando && (
            <Stack gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {nomeCaminho(editando)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Meta atual: <strong>{moeda(ajustada(editando.id))}</strong>
                </Typography>
              </Box>

              <TextField
                type="text"
                inputMode={modoEdicao === "valor" ? "numeric" : "decimal"}
                label={modoEdicao === "valor" ? "Nova meta (R$)" : "Variação sobre a meta atual (%)"}
                fullWidth
                value={campoEdicao}
                // O campo abre preenchido (meta atual ou "0") com o cursor no fim, e
                // "0-" não passa na máscara — então o conteúdo já vem selecionado para
                // a primeira tecla substituí-lo, e um "-" digitado após o "0" inicial
                // é aceito como começo de percentual negativo.
                autoFocus
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  let valor = e.target.value;
                  if (modoEdicao === "percentual" && valor === "0-") valor = "-";
                  // Em R$ só entram reais inteiros — a tela não mostra centavos, então
                  // também não deixa digitá-los; o percentual pode ter até duas casas.
                  const padrao = modoEdicao === "percentual" ? /^-?\d*[.,]?\d{0,2}$/ : /^\d*$/;
                  if (!padrao.test(valor)) return;
                  setCampoEdicao(valor);
                  setErroEdicao("");
                }}
              />

              <Alert severity={alvoValido ? "info" : "warning"} icon={false}>
                {alvoValido
                  ? `Nova meta: ${moeda(alvo)} · Diferença: ${moeda(alvo - ajustada(editando.id))}`
                  : "Informe um valor válido."}
              </Alert>

              <Typography variant="caption" color="text.secondary">
                {editando.tipoNodo === "PRODUTO"
                  ? "A alteração deste produto atualiza todas as categorias superiores, a loja e o total global."
                  : "O novo valor fixa a meta deste ramo: todos os níveis abaixo dele são ajustados na mesma proporção, e os totais acima acompanham a diferença."}
              </Typography>

              {erroEdicao && <Alert severity="error">{erroEdicao}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditando(null)}>Cancelar</Button>
          <Button variant="contained" onClick={aplicarEdicao} disabled={!alvoValido}>
            Aplicar e recalcular
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editandoGrupo} onClose={() => setEditandoGrupo(null)} fullWidth maxWidth="sm">
        <DialogTitle>Ajustar meta do grupo</DialogTitle>
        <DialogContent dividers>
          {editandoGrupo && (
            <Stack gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {editandoGrupo.nivel === 0 ? "Todas as lojas" : `${visaoGrupo.rotuloNivelGrupo(editandoGrupo)} ${editandoGrupo.nome}`} ·{" "}
                  {editandoGrupo.membros.length} {editandoGrupo.membros.length === 1 ? "loja" : "lojas"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Meta atual do grupo: <strong>{moeda(valorAtualGrupo(editandoGrupo))}</strong>
                </Typography>
              </Box>

              <TextField
                type="text"
                inputMode={modoGrupo === "valor" ? "numeric" : "decimal"}
                label={modoGrupo === "valor" ? "Nova meta do grupo (R$)" : "Participação em cada loja (%)"}
                fullWidth
                value={campoGrupo}
                autoFocus
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const valor = e.target.value;
                  const padrao = modoGrupo === "valor" ? /^\d*$/ : /^\d*[.,]?\d{0,2}$/;
                  if (!padrao.test(valor)) return;
                  setCampoGrupo(valor);
                  setErroGrupo("");
                }}
              />

              <Alert severity={alvosGrupoValidos ? "info" : "warning"} icon={false}>
                {alvosGrupoValidos && alvosDoGrupo
                  ? `Nova meta do grupo: ${moeda(alvosDoGrupo.reduce((acc, v) => acc + v, 0))} · Diferença: ${moeda(
                      alvosDoGrupo.reduce((acc, v) => acc + v, 0) - valorAtualGrupo(editandoGrupo),
                    )}`
                  : modoGrupo === "valor"
                    ? "Informe um valor válido."
                    : "Informe uma participação de 0% até menos de 100%."}
              </Alert>

              <Typography variant="caption" color="text.secondary">
                {modoGrupo === "valor"
                  ? "O novo valor é redistribuído entre as lojas na proporção do que cada uma tem hoje neste grupo. Em cada loja, os níveis abaixo são ajustados na mesma proporção e os totais acima acompanham a diferença."
                  : "A participação digitada passa a valer em todas as lojas, sobre o valor do nível acima de cada uma. Os demais itens do mesmo nível não são alterados, então o total acima acompanha a diferença."}
              </Typography>

              {erroGrupo && <Alert severity="error">{erroGrupo}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditandoGrupo(null)}>Cancelar</Button>
          <Button variant="contained" onClick={aplicarEdicaoGrupo} disabled={!alvosGrupoValidos}>
            Aplicar e recalcular
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!historicoNo} onClose={() => setHistoricoNo(null)} fullWidth maxWidth="sm">
        <DialogTitle>Histórico de alterações</DialogTitle>
        <DialogContent dividers>
          {historicoNo && (
            <Stack gap={2}>
              <Typography variant="caption" color="text.secondary">
                {nomeCaminho(historicoNo)}
              </Typography>

              {carregandoHistorico ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : !historicoItens?.length ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma alteração registrada para este valor.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Usuário</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell align="right">Valor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historicoItens.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>{item.usuario}</TableCell>
                        <TableCell>{new Date(item.data).toLocaleString("pt-BR")}</TableCell>
                        <TableCell align="right">{moeda(reaisInteiros(item.valor))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoricoNo(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        open={!!confirmando}
        onClose={() => setConfirmando(null)}
        onConfirm={onConfirmarAcao}
        title={confirmando?.acao === "publicar" ? "Publicar com ajustes não salvos" : "Trocar de cenário"}
        text={
          confirmando?.acao === "publicar"
            ? "Há ajustes que ainda não foram salvos e eles não serão considerados na publicação. Deseja publicar mesmo assim?"
            : "Há ajustes que ainda não foram salvos e eles serão perdidos ao trocar de cenário. Deseja continuar?"
        }
        btnText={confirmando?.acao === "publicar" ? "Publicar" : "Trocar"}
      />
    </Stack>
  );
}
